import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { cn } from "../utils";

const Tabs = TabsPrimitive.Root;

// useLayoutEffect warns "does nothing on the server" during SSR (Tabs is
// consumed by website-features under Next). Fall back to useEffect on the
// server; in the browser it stays a layout effect so the pill is measured and
// positioned before paint (no flicker).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

type PillRect = { left: number; top: number; width: number; height: number };

const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  // The active "pill" is a single shared element that slides to sit behind the
  // active trigger, rather than each trigger painting its own background (which
  // would snap rather than slide). `null` until we've measured an active tab.
  const [pill, setPill] = React.useState<PillRect | null>(null);

  // Merge the forwarded ref with our internal measuring ref.
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  useIsomorphicLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const update = () => {
      const active = list.querySelector<HTMLElement>('[data-state="active"]');
      if (!active) {
        setPill(null);
        return;
      }
      setPill({
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      });
    };

    update();

    // data-state flips on tab change (works for controlled & uncontrolled);
    // ResizeObserver catches container/font resize and flex-wrap reflow.
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(list, {
      attributes: true,
      attributeFilter: ["data-state"],
      subtree: true,
    });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(list);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <TabsPrimitive.List
      ref={setRefs}
      className={cn(
        // shadcn-style segmented control: a muted track holding the triggers.
        // `relative` anchors the sliding active pill. w-fit keeps it
        // content-sized (pass w-full to span); flex-wrap lets it break onto
        // multiple rows when there are too many tabs to fit on one line.
        "relative inline-flex w-fit max-w-full flex-wrap items-center justify-center gap-1 rounded-lg bg-gray-100 p-1 text-gray-600",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute z-0 rounded-md bg-white shadow-sm transition-all duration-200 ease-out motion-reduce:transition-none"
        style={
          pill
            ? {
                left: pill.left,
                top: pill.top,
                width: pill.width,
                height: pill.height,
                opacity: 1,
              }
            : { opacity: 0 }
        }
      />
      {children}
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // relative + z-10 keeps the label above the shared sliding pill in TabsList
      "relative z-10 inline-flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-transparent px-3 py-1 text-sm font-semibold ring-offset-white transition-all",
      "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      "text-gray-600 hover:text-primary",
      // the raised white pill is rendered once in TabsList and slides between
      // triggers; here we only flip the label colour for the active tab.
      "data-[state=active]:text-primary",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // blur-and-fade the panel in each time its tab becomes active
      // (fade-blur-in keyframe defined in theme.css); disabled for users who
      // prefer reduced motion — the panel just appears
      "mt-2 outline-hidden data-[state=active]:animate-fade-blur-in motion-reduce:animate-none",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
